import { type CSSProperties, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer } from '@helios/ui';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { qHomeCollectionsDetail, type HomeCollectionsDetailRow } from '@helios/data-access';
import {
  DrawerSummaryBanner,
  DrawerRowCard,
  DrawerFooterButton,
  DrawerEmptyState,
  PatientAvatar,
  StatusBadge,
  RowCTAButton,
  fmtDrawerTime,
  paymentMethodLabel,
} from './shared';
import { useFormatMoney } from '../../hooks/useFormatMoney';

export interface CollectionsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dayStartUtc: string;
  dayEndUtc: string;
  portalStyle?: CSSProperties;
}

export function CollectionsDrawer({
  open,
  onOpenChange,
  dayStartUtc,
  dayEndUtc,
  portalStyle,
}: CollectionsDrawerProps) {
  const navigate = useNavigate();
  const fmtCompact = useFormatMoney({ compact: true });
  const fmtCurrency = (amount: number) => fmtCompact(Math.round(amount));
  const { data: rows = [] } = usePowerSyncQuery<HomeCollectionsDetailRow>(
    qHomeCollectionsDetail(dayStartUtc, dayEndUtc),
  );

  const totalCollected = useMemo(
    () => rows.reduce((sum, r) => sum + (r.grand_total ?? 0), 0),
    [rows],
  );

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Collections Today"
      description="Payment transactions"
      width={520}
      portalStyle={portalStyle}
      footer={
        <DrawerFooterButton
          label="Open Billing"
          color="#059669"
          onClick={() => {
            navigate('/clinic/queue');
            onOpenChange(false);
          }}
        />
      }
    >
      <DrawerSummaryBanner
        icon="credit-card"
        iconBg="#ecfdf5"
        iconColor="#059669"
        value={fmtCurrency(totalCollected)}
        description={`${rows.length} payment${rows.length !== 1 ? 's' : ''} received`}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3, 12px)',
          padding: 'var(--sp-4, 16px)',
        }}
      >
        {rows.length === 0 && <DrawerEmptyState message="No collections today" />}

        {rows.map((row) => (
          <DrawerRowCard key={row.invoice_id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3, 12px)' }}>
              <PatientAvatar name={row.patient_name} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm, 13px)',
                    fontWeight: 'var(--font-weight-semi, 600)',
                    color: 'var(--text, #111827)',
                  }}
                >
                  {row.patient_name ?? 'Unknown'}
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs, 12px)',
                    color: 'var(--text-muted, #6b7280)',
                  }}
                >
                  {fmtDrawerTime(row.updated_at)} • {row.invoice_no ?? '—'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-base, 14px)',
                    fontWeight: 'var(--font-weight-semi, 600)',
                    color: 'var(--text, #111827)',
                  }}
                >
                  {fmtCurrency(row.grand_total)}
                </div>
                <StatusBadge label={paymentMethodLabel(row.payment_method)} />
              </div>
            </div>

            <RowCTAButton
              label="View Receipt"
              variant="outline"
              onClick={() => {
                const base = `/patient/${row.patient_id}/encounters`;
                const path = row.encounter_id
                  ? `${base}/${row.encounter_id}?tab=invoices&invoiceId=${row.invoice_id}`
                  : base;
                navigate(path);
                onOpenChange(false);
              }}
            />
          </DrawerRowCard>
        ))}
      </div>
    </Drawer>
  );
}
